import * as React from 'react'
import { BirthDateDraft, BirthDateOptions, BirthInputResult, normalizeBirthDateDraft, parseBirthDate } from '../../helpers/birthDateTime'
import { cn } from '../../helpers/utils'
import { useBirthInputMessages } from './birth-input-messages'
import { Input } from './input'

export type BirthDateInputProps = BirthDateOptions & {
  id: string
  value: BirthDateDraft
  onValueChange: (draft: BirthDateDraft, result: BirthInputResult) => void
  language?: string
  disabled?: boolean
  label?: string
  help?: string
  error?: string
  /** Set on a submission attempt when the page uses custom form validation. */
  showErrors?: boolean
  autoComplete?: 'off' | 'birthday'
  className?: string
}

export function BirthDateInput({ id, value, onValueChange, language, required = false,
  min, max, disabled, label, help, error, showErrors = false, autoComplete = 'off', className,
}: BirthDateInputProps) {
  const { text, monthFirst, errorText } = useBirthInputMessages('date', language)
  const [validated, setValidated] = React.useState(false)
  const inputs = React.useRef<Partial<Record<keyof BirthDateDraft, HTMLInputElement | null>>>({})
  const result = parseBirthDate(value, { required, min, max })
  const validationError = error || errorText(result.error)
  const visibleError = (showErrors || validated) ? validationError : undefined
  const description = `${id}-help${visibleError ? ` ${id}-error` : ''}`
  const order: (keyof BirthDateDraft)[] = monthFirst ? ['month', 'day', 'year'] : ['day', 'month', 'year']

  React.useEffect(() => {
    Object.values(inputs.current).forEach(input => input?.setCustomValidity(validationError ?? ''))
  }, [validationError])

  return (
    <fieldset id={id} disabled={disabled} className={cn('min-w-0 space-y-2', className)}
      aria-describedby={description} aria-invalid={visibleError ? true : undefined}
      onBlur={event => {
        if (event.currentTarget.contains(event.relatedTarget as Node | null)) return
        setValidated(true)
        const next = normalizeBirthDateDraft(value, { required, min, max })
        if (next.day !== value.day || next.month !== value.month || next.year !== value.year) {
          onValueChange(next, parseBirthDate(next, { required, min, max }))
        }
      }}>
      <legend className="text-sm font-medium">{label ?? text('date')}</legend>
      <p id={`${id}-help`} className="text-sm text-muted-foreground">{help ?? text('dateHelp')}</p>
      <div className="flex flex-wrap gap-3">
        {order.map(part => (
          <div key={part} className={part === 'year' ? 'w-24' : 'w-16'}>
            <label htmlFor={`${id}-${part}`} className="mb-1 block text-sm font-medium">{text(part)}</label>
            <Input id={`${id}-${part}`} ref={input => { inputs.current[part] = input }}
              type="text" inputMode="numeric" autoComplete={autoComplete === 'birthday' ? `bday-${part}` : 'off'}
              required={required} disabled={disabled} spellCheck={false} value={value[part]}
              aria-describedby={description} aria-invalid={visibleError ? true : undefined}
              className="min-h-[44px] text-base md:text-base tabular-nums"
              onInvalid={() => setValidated(true)}
              onChange={event => {
                setValidated(false)
                const next = { ...value, [part]: event.target.value }
                onValueChange(next, parseBirthDate(next, { required, min, max }))
              }} />
          </div>
        ))}
      </div>
      {visibleError && <p id={`${id}-error`} role="alert" className="text-sm text-destructive">{visibleError}</p>}
    </fieldset>
  )
}
